import type { TableProps } from "antd/es/table";

export type SortDirection = "asc" | "desc";

export interface SortState {
  key: string;
  order: SortDirection;
}

export type HeaderSortOrder = "ascend" | "descend";

export const parseSortStateFromQuery = (
  value: string | null,
  keyByAlias: Map<string, string>
): SortState | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  const isDescending = normalized.startsWith("-");
  const alias = (isDescending ? normalized.slice(1) : normalized).trim().toLowerCase();
  if (!alias) {
    return null;
  }

  const key = keyByAlias.get(alias);
  if (!key) {
    return null;
  }

  return {
    key,
    order: isDescending ? "desc" : "asc",
  };
};

export const encodeSortStateForQuery = (
  sortState: SortState,
  aliasByKey: Map<string, string>
): string | null => {
  const alias = aliasByKey.get(sortState.key);
  if (!alias) {
    return null;
  }

  const token = alias.toUpperCase();
  return sortState.order === "desc" ? `-${token}` : token;
};

export const parseLegacySortStateFromQuery = (value: string | null): SortState | null => {
  if (!value) {
    return null;
  }

  const [keyRaw, orderRaw] = value.split(":", 2);
  const key = keyRaw?.trim();
  if (!key) {
    return null;
  }

  if (orderRaw !== "ascend" && orderRaw !== "descend") {
    return null;
  }

  return {
    key,
    order: orderRaw === "descend" ? "desc" : "asc",
  };
};

export const isSameSortState = (left: SortState | null, right: SortState | null): boolean => {
  if (!left && !right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  return left.key === right.key && left.order === right.order;
};

export const extractSortStateFromTableSorter = <T extends object>(
  sorter: Parameters<NonNullable<TableProps<T>["onChange"]>>[2]
): SortState | null => {
  const activeSorter = Array.isArray(sorter) ? sorter.find((entry) => Boolean(entry.order)) : sorter;

  if (!activeSorter?.order) {
    return null;
  }

  const key =
    typeof activeSorter.columnKey === "string" || typeof activeSorter.columnKey === "number"
      ? String(activeSorter.columnKey)
      : typeof activeSorter.field === "string" || typeof activeSorter.field === "number"
        ? String(activeSorter.field)
        : null;

  if (!key) {
    return null;
  }

  return {
    key,
    order: activeSorter.order === "descend" ? "desc" : "asc",
  };
};

export const toHeaderSortOrder = (order: SortDirection): HeaderSortOrder => {
  return order === "desc" ? "descend" : "ascend";
};
